import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { cn } from "@/utils/cn";
import { useDropzone } from "react-dropzone";
import { useFormik, type FormikProps } from "formik";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import * as yup from "yup";
import { useGetAllUserDepartmentsQuery } from "@/services/(company)/departmentsApi";
import { useGetBranchesQuery } from "@/services/(company)/branchesApi";
import { useSelector } from "react-redux";
import { selectUser } from "@/store/slices/user.slice";
import { Textarea } from "@/components/ui/textarea";
import UploadViaQRCode from "@/components/UploadViaQRCode";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import { useEffect, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import {
  Building2,
  CalendarRange,
  FolderKanban,
  ImageIcon,
  ImageUp,
  Users,
  X,
} from "lucide-react";
import { mbToBytes } from "@/utils/helper";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Spinner } from "@/components/ui/v2/spinner";
import { PATHS } from "@/routes/paths";
import FormWrapper from "@/components/common/FormWrapper";
import { ENUM_PROJECT_STATUSES } from "@/types/(company)/projects.interface";
import { startCase } from "lodash";
import { ProjectManagersField } from "./project-managers-field";
import { StartEndDateField } from "./start-end-date-field";
import {
  useCreateProjectMutation,
  useGetProjectDetailsByIdQuery,
  useUpdateProjectMutation,
} from "@/services/(company)/api.projects";

const buildFormSchema = (t: TFunction) =>
  yup.object().shape(
    {
      name: yup
        .string()
        .required(t("projects:name_is_required"))
        .min(3, t("projects:name_must_be_at_least_3_characters"))
        .max(50, t("projects:name_must_be_less_than_50_characters")),
      description: yup
        .string()
        .required(t("projects:description_is_required"))
        .min(10, t("projects:description_must_be_at_least_10_characters")),
      branchId: yup.string().required(t("projects:please_select_a_branch")),
      departmentId: yup
        .string()
        .required(t("projects:please_select_a_department")),
      projectManagerIds: yup
        .array()
        .of(yup.number())
        .min(1, t("projects:select_at_least_one_manager")),
      startDate: yup
        .date()
        .nullable()
        .required(t("projects:start_date_is_required")),
      endDate: yup.date().nullable().optional(),
      projectOwner: yup
        .string()
        .required(t("projects:name_of_the_project_owner_is_required"))
        .min(3, t("projects:must_be_at_least_3_characters"))
        .max(50, t("projects:must_be_less_than_50_characters")),
      status: yup
        .string()
        .oneOf(Object.values(ENUM_PROJECT_STATUSES))
        .required(t("projects:please_select_a_status")),
      image: yup
        .mixed()
        .test("is-valid", t("validation:required"), (val, ctx) => {
          if (ctx.parent.fileId) return true;
          return val instanceof File || typeof val === "string";
        }),
      fileId: yup.string().nullable(),
    },
    [["image", "fileId"]],
  );

export const TEAM_ID_SEARCHPARAM_KEY = "tId";

export type Fields = yup.InferType<ReturnType<typeof buildFormSchema>>;
const fieldNames: Record<keyof Fields, keyof Fields> = {
  name: "name",
  description: "description",
  branchId: "branchId",
  departmentId: "departmentId",
  image: "image",
  fileId: "fileId",
  projectManagerIds: "projectManagerIds",
  endDate: "endDate",
  startDate: "startDate",
  status: "status",
  projectOwner: "projectOwner",
};

const FormSection = ({
  icon,
  title,
  accent,
  children,
}: {
  icon: ReactNode;
  title: string;
  accent: string;
  children: ReactNode;
}) => (
  <div className="rounded-xs border border-gray-100 bg-white shadow-sm overflow-hidden">
    <div
      className={cn(
        "flex items-center gap-2.5 px-4 py-3 border-b border-gray-100",
        accent,
      )}
    >
      <span className="opacity-80">{icon}</span>
      <span className="text-sm font-semibold text-gray-700">{title}</span>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

export const CreateProjectForm = ({
  projectId,
  isUpdating,
}: {
  projectId?: string;
  isUpdating: boolean;
}) => {
  const { t } = useTranslation(["projects", "common", "validation"]);
  const navigate = useNavigate();
  const {
    data: { data: projectDetails } = {},
    isFetching: isFetchingTeamDetails,
  } = useGetProjectDetailsByIdQuery(
    { projectId: projectId! },
    { skip: !isUpdating, refetchOnMountOrArgChange: true },
  );

  const [createProject, { isLoading: isCreating }] = useCreateProjectMutation();
  const [updateProject, { isLoading: isUpdatingTeam }] =
    useUpdateProjectMutation();

  const {
    data: { data: departments } = {},
    isFetching: isFetchingDepartments,
  } = useGetAllUserDepartmentsQuery({});
  const { data: branchesData, isFetching: isFetchingBranches } =
    useGetBranchesQuery();
  const currentUser = useSelector(selectUser).user;

  const formik = useFormik<Fields>({
    enableReinitialize: true,
    validationSchema: buildFormSchema(t),
    initialValues: {
      name: projectDetails?.name || "",
      image: projectDetails?.image || "",
      description: projectDetails?.description || "",
      branchId: projectDetails?.branchId
        ? projectDetails.branchId.toString()
        : currentUser?.branchId
          ? String(currentUser.branchId)
          : "",
      departmentId: projectDetails?.departmentId.toString() || "",
      startDate: projectDetails?.startDate
        ? new Date(projectDetails.startDate)
        : (null as unknown as Date),
      endDate: projectDetails?.endDate
        ? new Date(projectDetails.endDate)
        : null,
      projectManagerIds:
        projectDetails?.projectManagers.map((manager) => manager.id) ?? [],
      status: projectDetails?.status || ENUM_PROJECT_STATUSES.NOT_STARTED,
      projectOwner: projectDetails?.projectOwner || "",
      fileId: "",
    },
    onSubmit: async (values) => {
      const payload = new FormData();
      Object.entries(values).forEach(([key, value]) => {
        if (key === fieldNames.image && values.fileId) return;
        if (value instanceof File) payload.append(key, value);
        if (value instanceof Date) payload.append(key, value.toISOString());
        else if (value !== null && value !== undefined)
          payload.append(key, String(value));
      });
      if (projectId && isUpdating) {
        await updateProject({ projectId, payload })
          .unwrap()
          .then((res) => {
            toast.success(res?.message);
            navigate(PATHS.COMPANY.COMPANY_PROJECTS_PATHS.VIEW(projectId));
          })
          .catch((e) => toast.error(e?.message ?? e?.response?.message));
      } else {
        await createProject(payload)
          .unwrap()
          .then((res) => {
            toast.success(res?.message);
            navigate(PATHS.COMPANY.COMPANY_PROJECTS_PATHS.VIEW(res.data.id));
          })
          .catch((e) =>
            toast.error(e?.data?.message ?? t("common:states.error_title")),
          );
      }
    },
  });

  if (isFetchingTeamDetails) return <Spinner />;

  return (
    <FormWrapper
      onSubmit={formik.handleSubmit}
      title={
        isUpdating
          ? (projectDetails?.name ?? t("projects:edit_project"))
          : t("projects:create_project")
      }
      subtitle={
        isUpdating
          ? t("projects:modify_the_basic_details_of_the_project")
          : t("projects:enter_the_basic_details_of_the_project")
      }
      submitText={t("projects:save")}
      loading={isCreating || isUpdatingTeam}
    >
      <form
        onSubmit={formik.handleSubmit}
        className="flex flex-col lg:flex-row gap-5"
      >
        {/* ── Left column: form sections ── */}
        <div className="flex-1 flex flex-col gap-4 min-w-0">
          {/* Section 1: Project Identity */}
          <FormSection
            icon={<FolderKanban size={16} className="text-primary" />}
            title={t("projects:project_identity")}
            accent="bg-primary/5"
          >
            <div className="flex flex-col gap-4">
              <Field>
                <FieldLabel>
                  {t("projects:project_name")}
                  <span className="text-danger-500">*</span>
                </FieldLabel>
                <Input
                  id={fieldNames.name}
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  placeholder={t("projects:e_g_q3_marketing_campaign")}
                  className={cn(
                    formik.touched.name &&
                      formik.errors.name &&
                      "border-danger-500",
                  )}
                />
                <FieldError>
                  {formik.touched.name && formik.errors.name}
                </FieldError>
              </Field>

              <Field>
                <FieldLabel>
                  {t("projects:description")}
                  <span className="text-danger-500">*</span>
                </FieldLabel>
                <Textarea
                  id={fieldNames.description}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  placeholder={t(
                    "projects:describe_the_project_goals_scope_and_expected_outcomes",
                  )}
                  rows={4}
                  className={cn(
                    formik.touched.description &&
                      formik.errors.description &&
                      "border-danger-500",
                  )}
                />
                <FieldError>
                  {formik.touched.description && formik.errors.description}
                </FieldError>
              </Field>
            </div>
          </FormSection>

          {/* Section 2: Organization */}
          <FormSection
            icon={<Building2 size={16} className="text-blue-500" />}
            title={t("projects:organization")}
            accent="bg-blue-50"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field>
                <FieldLabel htmlFor={fieldNames.branchId}>
                  {t("common:ui_kit.branch")}
                  <span className="text-danger-500">*</span>
                </FieldLabel>
                <Select
                  disabled={isFetchingBranches}
                  value={formik.values.branchId}
                  onValueChange={(value) =>
                    value && formik.setFieldValue(fieldNames.branchId, value)
                  }
                >
                  <SelectTrigger id={fieldNames.branchId}>
                    <SelectValue placeholder={t("projects:select_a_branch")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {branchesData?.data?.map((branch) => (
                        <SelectItem
                          key={branch.id}
                          value={branch.id.toString()}
                        >
                          {branch.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FieldError>
                  {formik.touched.branchId && formik.errors.branchId}
                </FieldError>
              </Field>

              <Field>
                <FieldLabel htmlFor={fieldNames.departmentId}>
                  {t("common:labels.department")}
                  <span className="text-danger-500">*</span>
                </FieldLabel>
                <Select
                  disabled={isFetchingDepartments}
                  value={formik.values.departmentId}
                  onValueChange={(value) =>
                    value &&
                    formik.setFieldValue(fieldNames.departmentId, value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t("projects:select_a_department")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {departments?.map((dept) => (
                        <SelectItem key={dept.id} value={dept.id.toString()}>
                          {dept.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FieldError>
                  {formik.touched.departmentId && formik.errors.departmentId}
                </FieldError>
              </Field>
            </div>
          </FormSection>

          {/* Section 3: Team & Status */}
          <FormSection
            icon={<Users size={16} className="text-emerald-600" />}
            title={t("projects:team_and_status")}
            accent="bg-emerald-50"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field>
                <FieldLabel>
                  {t("projects:project_owner")}
                  <span className="text-danger-500">*</span>
                </FieldLabel>
                <Input
                  id={fieldNames.projectOwner}
                  value={formik.values.projectOwner}
                  onChange={formik.handleChange}
                  placeholder={t("projects:full_name_of_the_owner")}
                  className={cn(
                    formik.touched.projectOwner &&
                      formik.errors.projectOwner &&
                      "border-danger-500",
                  )}
                />
                <FieldError>
                  {formik.touched.projectOwner && formik.errors.projectOwner}
                </FieldError>
              </Field>

              <ProjectManagersField formik={formik} />

              <Field className="col-span-full">
                <FieldLabel htmlFor={fieldNames.status}>
                  {t("projects:status")}
                  <span className="text-danger-500">*</span>
                </FieldLabel>
                <Select
                  value={formik.values.status}
                  onValueChange={(value) =>
                    value && formik.setFieldValue(fieldNames.status, value)
                  }
                >
                  <SelectTrigger id={fieldNames.status}>
                    <SelectValue placeholder={t("projects:select_a_status")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {Object.values(ENUM_PROJECT_STATUSES).map((status) => (
                        <SelectItem key={status} value={status}>
                          {t(`common:status.${status}`, {
                            defaultValue: startCase(status),
                          })}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FieldError>
                  {formik.touched.status && formik.errors.status}
                </FieldError>
              </Field>
            </div>
          </FormSection>

          {/* Section 4: Timeline */}
          <FormSection
            icon={<CalendarRange size={16} className="text-orange-500" />}
            title={t("projects:timeline")}
            accent="bg-orange-50"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <StartEndDateField
                formik={formik}
                fieldName={fieldNames.startDate}
                label={t("projects:start_date")}
              />
              <StartEndDateField
                formik={formik}
                fieldName={fieldNames.endDate}
                label={t("projects:end_date_optional")}
              />
            </div>
          </FormSection>
        </div>

        {/* ── Right column: project cover ── */}
        <div className="lg:w-72 shrink-0">
          <div className="sticky top-0">
            <ProjectLogoUploadField
              formik={formik}
              fieldName={fieldNames.image}
            />
          </div>
        </div>
      </form>
    </FormWrapper>
  );
};

const ProjectLogoUploadField = ({
  formik,
  fieldName,
}: {
  formik: FormikProps<Fields>;
  fieldName: keyof Fields;
}) => {
  const { t } = useTranslation(["projects", "common"]);
  const [preview, setPreview] = useState<string | null>(null);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { "image/*": [] },
    maxSize: mbToBytes(5),
    multiple: false,
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        formik.setFieldValue(fieldName, file);
        setPreview(URL.createObjectURL(file));
      }
    },
  });

  const handleClear = () => {
    formik.setFieldValue(fieldName, null);
    setPreview(null);
  };

  useEffect(() => {
    const value = formik.values[fieldName];
    if (typeof value === "string") setPreview(value);
    return () => {
      if (
        preview &&
        typeof preview === "string" &&
        preview.startsWith("blob:")
      ) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [formik.values, fieldName]);

  const hasError = Boolean(
    formik.touched[fieldName] && formik.errors[fieldName],
  );

  return (
    <div className="rounded-xs border border-gray-100 bg-white shadow-sm overflow-hidden">
      <div className="flex items-center gap-2.5 px-4 py-3 bg-gray-50 border-b border-gray-100">
        <ImageIcon size={16} className="text-gray-500" />
        <span className="text-sm font-semibold text-gray-700">
          {t("projects:project_cover")}{" "}
          <span className="text-danger-500">*</span>
        </span>
      </div>

      <div className="p-4">
        {preview ? (
          <div className="relative w-full aspect-square rounded-xs overflow-hidden border border-gray-200 bg-gray-50">
            <img
              src={preview}
              alt={t("projects:project_cover_preview")}
              className="w-full h-full object-cover"
            />
            <button
              title={t("projects:remove_image")}
              type="button"
              onClick={handleClear}
              className="absolute top-2 end-2 p-1.5 text-white bg-black/60 rounded-xs hover:bg-black/80 transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <div
            {...getRootProps()}
            className={cn(
              "w-full aspect-square rounded-xs border-2 border-dashed cursor-pointer transition-all flex flex-col items-center justify-center gap-3 text-center px-4",
              isDragActive
                ? "border-primary bg-primary/5 scale-[0.98]"
                : "border-gray-200 bg-gray-50 hover:border-primary/50 hover:bg-primary/5",
              hasError && "border-danger-500 bg-danger-50",
            )}
          >
            <input {...getInputProps()} />
            <div className="p-3 rounded-xs bg-primary/10">
              <ImageUp size={28} className="text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">
                {isDragActive
                  ? t("projects:drop_image_here")
                  : t("projects:drag_and_drop_or_click_to_upload")}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {t("projects:jpg_png_svg_max_5mb")}
              </p>
            </div>
            <Button type="button" variant="outline" size="sm" className="mt-1">
              {t("projects:choose_file")}
            </Button>
          </div>
        )}

        {hasError && (
          <p className="text-danger-500 text-xs mt-2">
            {String(formik.errors[fieldName])}
          </p>
        )}

        <div className="mt-3">
          <UploadViaQRCode
            fileType="image/*"
            moduleType={PLATFORM_SECTION_ENUM.PROJECTS}
            onUploadComplete={(data) => {
              formik.setFieldValue(fieldName, data.fileUrls.frontSideFileUrl);
              formik.setFieldValue(
                fieldNames.fileId,
                data.fileIds.frontSideFileId,
              );
            }}
          />
        </div>
      </div>
    </div>
  );
};
